import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { resolveApiUrl } from '../../services/apiConfig';

function InfoRow({ icon, label, value, href }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 bg-[#0B5D1E]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</p>
        {href && value
          ? <a href={href} className="text-sm font-medium text-[#0B5D1E] underline break-words">{value}</a>
          : <p className="text-sm font-medium text-gray-900 break-words">{value || <span className="text-gray-400 italic">Not provided</span>}</p>
        }
      </div>
    </div>
  );
}

export default function VerifyMember() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(resolveApiUrl(`/accounts/member/${slug}/`))
      .then(res => setProfile(res.data))
      .catch(() => setError('Member not found.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B5D1E]/5 via-white to-[#C8A951]/5 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0B5D1E]/20 border-t-[#0B5D1E]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl border border-red-100 p-10 text-center max-w-md w-full">
        <div className="w-20 h-20 mx-auto mb-5 bg-red-50 rounded-full flex items-center justify-center">
          <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Failed</h2>
        <p className="text-gray-500 mb-6">{error}</p>
        <Link to="/" className="inline-block bg-[#0B5D1E] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#062E10] transition text-sm">
          Go to Homepage
        </Link>
      </div>
    </div>
  );

  const isActive = profile.membership_status === 'active';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B5D1E]/5 via-white to-[#C8A951]/5 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">

          {/* Header */}
          <div className="bg-[#0B5D1E] px-6 py-5 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/APPSN.jpg" alt="APPSN" className="w-10 h-10 rounded-full bg-white/20 object-cover" />
              <img src="/Nigerian Institution of surveyors.jpg" alt="NIS" className="w-10 h-10 rounded-full bg-white/20 object-cover" />
            </div>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
              APPSN FCT BRANCH
            </h1>
            <p className="text-white/60 text-xs mt-0.5">Member Verification Card</p>
          </div>

          {/* NIS Status Banner */}
          <div className={`px-6 py-2.5 flex items-center justify-center gap-2 ${isActive ? 'bg-green-600' : 'bg-red-600'}`}>
            {isActive ? (
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span className="text-white text-xs font-bold uppercase tracking-widest">
              NIS Status: {isActive ? 'Active Member' : (profile.membership_status || 'Inactive')}
            </span>
          </div>

          {/* Passport Photo */}
          <div className="flex flex-col items-center pt-6 pb-3 px-6">
            {profile.photo ? (
              <img
                src={profile.photo}
                alt={profile.full_name}
                className="w-28 h-28 rounded-full object-cover border-4 border-[#0B5D1E] shadow-lg"
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-[#0B5D1E]/10 border-4 border-[#0B5D1E] flex items-center justify-center text-4xl font-bold text-[#0B5D1E] shadow-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                {profile.full_name?.charAt(0)}
              </div>
            )}
            <h2 className="text-xl font-bold text-gray-900 mt-3 text-center" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {profile.full_name}
            </h2>
            {profile.membership_category && (
              <span className="mt-1 px-3 py-0.5 bg-[#C8A951]/15 text-[#8a6d10] text-xs font-semibold rounded-full border border-[#C8A951]/30">
                {profile.membership_category}
              </span>
            )}
          </div>

          {/* Member Details */}
          <div className="px-6 pb-5 space-y-3">
            <div className="bg-gray-50 rounded-xl p-4 space-y-4 border border-gray-100">

              {/* Company Name */}
              <InfoRow
                label="Company Name"
                value={profile.firm_name}
                icon={
                  <svg className="w-4 h-4 text-[#0B5D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                }
              />

              <div className="border-t border-gray-200" />

              {/* SURCON No */}
              <InfoRow
                label="SURCON No."
                value={profile.surcon_number}
                icon={
                  <svg className="w-4 h-4 text-[#0B5D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                  </svg>
                }
              />

              <div className="border-t border-gray-200" />

              {/* Shortcode */}
              <InfoRow
                label="Shortcode"
                value={profile.shortcode}
                icon={
                  <svg className="w-4 h-4 text-[#0B5D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                }
              />

              <div className="border-t border-gray-200" />

              {/* Email */}
              <InfoRow
                label="Email"
                value={profile.email}
                href={profile.email ? `mailto:${profile.email}` : undefined}
                icon={
                  <svg className="w-4 h-4 text-[#0B5D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
              />

              <div className="border-t border-gray-200" />

              {/* Phone */}
              <InfoRow
                label="Phone"
                value={profile.phone_number}
                href={profile.phone_number ? `tel:${profile.phone_number}` : undefined}
                icon={
                  <svg className="w-4 h-4 text-[#0B5D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                }
              />
            </div>

            {/* Verification Badge */}
            {isActive ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <img src="/Verified.png" alt="Verified" className="w-9 h-9 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-green-800">Verified Active Member</p>
                  <p className="text-xs text-green-600">This surveyor is a verified member of APPSN FCT Branch</p>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <svg className="w-9 h-9 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="text-sm font-bold text-red-800">Membership Not Active</p>
                  <p className="text-xs text-red-600">Current status: {profile.membership_status}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t px-6 py-4 text-center">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
              Association of Private Practice Surveyors of Nigeria
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">FCT Branch &bull; Membership Verification</p>
          </div>
        </div>

        <div className="text-center mt-5">
          <Link to="/" className="text-[#0B5D1E] hover:text-[#C8A951] text-sm font-semibold transition">
            Visit APPSN FCT Website &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
}
